import { useRef, useState } from 'react';
import type { TransferSegment } from '../../types';
import { fromMinor, money, toMinor } from '../../core';
import { newId, useActions } from '../../store';
import { TextField, NumberField, MoneyField, Checkbox, NoteField } from './fields';
import fieldStyles from './fields.module.css';
import formShell from './formShell.module.css';
import { useT } from '../../i18n';

export interface TransferFormProps {
  mode: 'create' | 'edit';
  segment?: TransferSegment;
  onClose(): void;
}

export function TransferForm({ mode, segment, onClose }: TransferFormProps) {
  const t = useT();
  const actions = useActions();

  const [from, setFrom] = useState(segment?.from ?? '');
  const [to, setTo] = useState(segment?.to ?? '');
  const [durationMin, setDurationMin] = useState(
    segment ? String(segment.durationMin) : '',
  );
  const [amount, setAmount] = useState(segment ? String(fromMinor(segment.price.amount)) : '');
  const [currency, setCurrency] = useState(segment?.price.currency ?? 'USD');
  const [perPerson, setPerPerson] = useState(segment?.perPerson ?? false);
  const [note, setNote] = useState(segment?.note ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const refs = useRef<Record<string, HTMLElement | null>>({});

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!from.trim()) errs.from = t('error.fromRequired');
    if (!to.trim()) errs.to = t('error.toRequired');
    const durNum = parseInt(durationMin, 10);
    if (durationMin.trim() === '' || Number.isNaN(durNum) || durNum < 0) {
      errs.durationMin = t('error.durationNegative');
    }
    const amountNum = parseFloat(amount.replace(',', '.'));
    if (amount.trim() === '' || Number.isNaN(amountNum) || amountNum < 0) {
      errs.amount = t('error.priceNegative');
    }
    return errs;
  }

  function focusFirstError(errs: Record<string, string>) {
    for (const key of ['from', 'to', 'durationMin', 'amount']) {
      if (errs[key]) {
        refs.current[key]?.focus();
        return;
      }
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstError(errs);
      return;
    }
    const built: TransferSegment = {
      kind: 'transfer',
      id: segment?.id ?? newId('t'),
      from: from.trim(),
      to: to.trim(),
      durationMin: parseInt(durationMin, 10),
      price: money(toMinor(amount), currency),
      perPerson,
      note: note.trim() || undefined,
    };
    if (mode === 'edit' && segment) {
      actions.updateSegment(built.id, built);
    } else {
      actions.addSegment(built);
    }
    onClose();
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className={fieldStyles.row}>
        <TextField
          id="transfer-from"
          label={t('field.from')}
          value={from}
          onChange={setFrom}
          required
          autoFocus
          error={errors.from}
          inputRef={(el) => {
            refs.current.from = el;
          }}
        />
        <TextField
          id="transfer-to"
          label={t('field.to')}
          value={to}
          onChange={setTo}
          required
          error={errors.to}
          inputRef={(el) => {
            refs.current.to = el;
          }}
        />
      </div>
      <NumberField
        id="transfer-duration"
        label={t('field.duration')}
        value={durationMin}
        onChange={setDurationMin}
        min={0}
        required
        error={errors.durationMin}
        inputRef={(el) => {
          refs.current.durationMin = el;
        }}
      />
      <MoneyField
        idPrefix="transfer-price"
        label={t('field.price')}
        amount={amount}
        onAmountChange={setAmount}
        currency={currency}
        onCurrencyChange={setCurrency}
        required
        error={errors.amount}
        inputRef={(el) => {
          refs.current.amount = el;
        }}
      />
      <Checkbox
        id="transfer-per-person"
        label={t('field.perPerson')}
        checked={perPerson}
        onChange={setPerPerson}
      />
      <NoteField id="transfer-note" value={note} onChange={setNote} />
      <div className={formShell.footer}>
        <button type="button" className="btn" onClick={onClose}>
          {t('form.cancel')}
        </button>
        <button type="submit" className="btn btn-primary">
          {t('form.save')}
        </button>
      </div>
    </form>
  );
}
